/**
 * Santo Studio - custom theme JS
 *
 * Consolidated:
 * - Swiper inits (defensive + section-aware)
 * - AOS init + refresh hooks
 * - Quick-view -> quick-add click bridge (delegated)
 *
 * Optional debug:
 *   window.SANTO_THEME_DEBUG = true
 */

(() => {
  const DEBUG = Boolean(window.SANTO_THEME_DEBUG);

  const debugLog = (...args) => {
    if (DEBUG) console.log(...args);
  };

  const debugError = (...args) => {
    if (DEBUG) console.error(...args);
  };

  const onReady = (cb) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
      cb();
    }
  };

  // -----------------
  // AOS
  // -----------------
  function initAOS(root = document) {
    if (!window.AOS) return;

    // Preserve your existing grouping behavior
    root
      .querySelectorAll(
        '.ss-collection-hero__brands__item, .ss-hero-slider-and-brands__brands__item',
      )
      .forEach((el) => el.setAttribute('data-aos-id', 'earlyBrands'));

    root
      .querySelectorAll('.ss-custom-image-blocks__item')
      .forEach((el) => el.setAttribute('data-aos-id', 'lateBlocks'));

    if (!initAOS._initialized) {
      try {
        window.AOS.init({ once: true });
        initAOS._initialized = true;
      } catch (e) {
        debugError('AOS.init failed', e);
      }
    }

    try {
      window.AOS.refreshHard();
    } catch (e) {
      // ignore
    }
  }
  initAOS._initialized = false;

  // -----------------
  // Swiper
  // -----------------
  function hasSwiper() {
    return typeof window.Swiper === 'function';
  }

  function createSwiper(swiperEl, options) {
    if (!swiperEl || !hasSwiper()) return null;

    // Swiper attaches instance to element
    if (swiperEl.swiper) return swiperEl.swiper;

    try {
      return new window.Swiper(swiperEl, options);
    } catch (e) {
      debugError('Swiper init failed', e);
      return null;
    }
  }

  function initSwipers(root = document) {
    if (!hasSwiper()) return;

    // PDP media swiper (only if present)
    root.querySelectorAll('.pdp-images .swiper').forEach((swiperEl) => {
      const scope =
        swiperEl.closest('.pdp-images') || swiperEl.parentElement || document;

      createSwiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 10,
        scrollbar: {
          el: scope.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        navigation: {
          nextEl: scope.querySelector('.swiper-button-next'),
          prevEl: scope.querySelector('.swiper-button-prev'),
        },
        pagination: {
          el: scope.querySelector('.swiper-pagination'),
          type: 'fraction',
        },
        breakpoints: {
          768: { slidesPerView: 1, spaceBetween: 20 },
          1024: { slidesPerView: 1, spaceBetween: 20 },
        },
      });
    });

    // Section: swiper_pdp
    root.querySelectorAll('.swiper_pdp .swiper').forEach((swiperEl) => {
      const scope =
        swiperEl.closest('.swiper_pdp') || swiperEl.parentElement || document;

      createSwiper(swiperEl, {
        slidesPerView: 1.5,
        spaceBetween: 0,
        scrollbar: {
          el: scope.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        breakpoints: {
          768: { slidesPerView: 1.5, spaceBetween: 0 },
          1024: { slidesPerView: 3, spaceBetween: 0 },
        },
      });
    });

    // Section: swiper_collection
    root.querySelectorAll('.swiper_collection .swiper').forEach((swiperEl) => {
      const scope =
        swiperEl.closest('.swiper_collection') ||
        swiperEl.parentElement ||
        document;

      createSwiper(swiperEl, {
        slidesPerView: 1.2,
        spaceBetween: 0,
        scrollbar: {
          el: scope.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        breakpoints: {
          768: { slidesPerView: 1.5, spaceBetween: 0 },
          1024: { slidesPerView: 2.1, spaceBetween: 0 },
        },
      });
    });

    // Section: swiper_blog
    root.querySelectorAll('.swiper_blog .swiper').forEach((swiperEl) => {
      const scope =
        swiperEl.closest('.swiper_blog') || swiperEl.parentElement || document;
      const wrapper = swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      const totalSlides = wrapper.querySelectorAll('.swiper-slide').length || 1;

      createSwiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 20,
        scrollbar: {
          el: scope.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        breakpoints: {
          768: { slidesPerView: 1, spaceBetween: 20 },
          1024: { slidesPerView: Math.max(1, totalSlides), spaceBetween: 20 },
        },
      });
    });
  }

  // -----------------
  // Quick view click bridge (delegated)
  // -----------------
  function initQuickViewDelegate() {
    if (initQuickViewDelegate._initialized) return;
    initQuickViewDelegate._initialized = true;

    document.addEventListener('click', (event) => {
      const quickView = event.target?.closest?.('.quick_view');
      if (!quickView) return;

      const cardWrapper = quickView.closest(
        '.card-wrapper.product-card-wrapper',
      );
      if (!cardWrapper) return;

      const quickAddButton = cardWrapper.querySelector(
        '.quick-add__submit.button.button--full-width.button--secondary',
      );

      if (quickAddButton) {
        event.preventDefault();
        quickAddButton.click();
      }
    });
  }
  initQuickViewDelegate._initialized = false;

  const preorderStateObservers = new WeakMap();
  const preorderStateSyncFrames = new WeakMap();
  const DEFAULT_FREE_SHIPPING_TEXT = 'FREE SHIPPING';

  function collectMainProductTemplates(root = document) {
    const templates = [];
    if (root && root.nodeType === 1 && root.matches('.main-product-template')) {
      templates.push(root);
    }
    root.querySelectorAll?.('.main-product-template').forEach((template) => {
      templates.push(template);
    });
    return templates;
  }

  function normalizeInlineText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function getAnnouncementFreeShippingText() {
    const announcedMessage =
      document
        .querySelector('.announcement-bar-section .utility-bar[data-free-shipping-message]')
        ?.getAttribute('data-free-shipping-message') || '';
    const normalizedAnnouncedMessage = normalizeInlineText(announcedMessage);
    if (normalizedAnnouncedMessage) {
      return normalizedAnnouncedMessage;
    }

    const messageNodes = Array.from(
      document.querySelectorAll(
        '.announcement-bar-section .announcement-bar__message span, .announcement-bar-section .announcement-bar__message',
      ),
    );
    if (!messageNodes.length) return '';

    for (const node of messageNodes) {
      const text = normalizeInlineText(node.textContent);
      if (!text) continue;
      const lowerText = text.toLowerCase();
      if (lowerText.includes('free') && lowerText.includes('ship')) {
        return text;
      }
    }

    return '';
  }

  function getTemplateFreeShippingText(template) {
    const announcementText = getAnnouncementFreeShippingText();
    if (announcementText) return announcementText;

    const summaryText = normalizeInlineText(
      template.querySelector('.pdp-free-shipping-summary__detail-text')?.textContent,
    );
    return summaryText || DEFAULT_FREE_SHIPPING_TEXT;
  }

  function syncFreeShippingSummaryText(template, text) {
    if (!text) return;
    template.querySelectorAll('.pdp-free-shipping-summary__detail-text').forEach((node) => {
      if (normalizeInlineText(node.textContent) !== text) {
        node.textContent = text;
      }
      // Reveal the summary now that the canonical (announcement-bar) text is in.
      // It's rendered cloaked server-side so the settings-built fallback text
      // doesn't paint first and then visibly swap to the announcement wording.
      node
        .closest('.pdp-free-shipping-summary')
        ?.classList.remove('pdp-free-shipping-summary--pending');
    });
  }

  function isVisibleElement(element) {
    if (!element) return false;
    if (element.hidden) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden')
      return false;
    if (element.getClientRects().length === 0) return false;
    return true;
  }

  function hasVisiblePreorderDescription(template) {
    const descriptions = Array.from(
      template.querySelectorAll('.restock-rocket-preorder-description'),
    );
    return descriptions.some((description) => {
      if (!isVisibleElement(description)) return false;
      const normalizedText =
        description.textContent?.replace(/\s+/g, ' ').trim() || '';
      return /pre[\s-]?order/i.test(normalizedText);
    });
  }

  function hasVisiblePreorderBadge(template) {
    const badges = Array.from(
      template.querySelectorAll(
        '.restock-rocket-preorder-badge.preorder-badge-product',
      ),
    );
    return badges.some((badge) => {
      if (badge.classList.contains('pdp-product-title__preorder-badge'))
        return false;
      if (badge.hidden) return false;
      if (badge.getAttribute('aria-hidden') === 'true') return false;
      const normalizedText =
        badge.textContent?.replace(/\s+/g, ' ').trim() || '';
      return /pre[\s-]?order/i.test(normalizedText);
    });
  }

  function hasVisibleDynamicCheckoutButton(template) {
    const paymentButtons = Array.from(
      template.querySelectorAll(
        '.product-form__buttons .shopify-payment-button',
      ),
    );
    return paymentButtons.some((paymentButton) => {
      if (!isVisibleElement(paymentButton)) return false;
      if (paymentButton.hidden) return false;
      if (paymentButton.getAttribute('aria-hidden') === 'true') return false;
      return true;
    });
  }

  function hasVisiblePreorderButton(template) {
    const buttons = Array.from(
      template.querySelectorAll(
        '.product-form__buttons .product-form__submit.product-section__sticky-button',
      ),
    );
    return buttons.some((button) => {
      if (!isVisibleElement(button)) return false;
      if (button.hidden) return false;
      if (button.getAttribute('aria-hidden') === 'true') return false;
      const normalizedText =
        button.textContent?.replace(/\s+/g, ' ').trim() || '';
      return /pre[\s-]?order/i.test(normalizedText);
    });
  }

  function getPreorderBadgeText(template) {
    const badges = Array.from(
      template.querySelectorAll(
        '.restock-rocket-preorder-badge.preorder-badge-product',
      ),
    );
    for (const badge of badges) {
      if (badge.classList.contains('pdp-product-title__preorder-badge'))
        continue;
      const normalizedText =
        badge.textContent?.replace(/\s+/g, ' ').trim() || '';
      if (/pre[\s-]?order/i.test(normalizedText)) {
        return normalizedText;
      }
    }
    // Keep the title badge that main-product.liquid renders server-side: once the
    // submit button reads "Preorder" from first paint, treat the product as preorder
    // even before Restock Rocket injects its own badge/description. Without this, the
    // first sync (pre-Rocket) would wipe the server badge and re-add it late — a jump.
    if (hasVisiblePreorderDescription(template) || hasVisiblePreorderButton(template)) {
      return 'PREORDER';
    }
    return '';
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function splitPreorderMessageLines(rawText) {
    const normalizedText = (rawText || '')
      .replace(/\s+/g, ' ')
      .replace(/^\s*note:\s*/i, '')
      .trim();
    if (!normalizedText) {
      return { firstLine: '', secondLine: '' };
    }
    const preorderMatch = normalizedText.match(
      /^(this is a pre[\s-]?order\.?)(.*)$/i,
    );
    if (preorderMatch) {
      let firstLine = preorderMatch[1].trim();
      if (!/[.!?]$/.test(firstLine)) {
        firstLine += '.';
      }
      const secondLine = preorderMatch[2].trim();
      return { firstLine, secondLine };
    }
    const sentenceMatch = normalizedText.match(/^(.+?[.!?])\s+(.*)$/);
    if (sentenceMatch) {
      return {
        firstLine: sentenceMatch[1].trim(),
        secondLine: sentenceMatch[2].trim(),
      };
    }
    return { firstLine: normalizedText, secondLine: '' };
  }

  function formatPreorderMessageHtml(rawText) {
    const { firstLine, secondLine } = splitPreorderMessageLines(rawText);
    const firstHtml = `<span class="preorder-description-line preorder-description-line--primary">${escapeHtml(firstLine)}</span>`;
    if (!secondLine) {
      return firstHtml;
    }
    return `${firstHtml}<span class="preorder-description-line preorder-description-line--secondary">${escapeHtml(secondLine)}</span>`;
  }

  function normalizePreorderDescriptionText(template, freeShippingText) {
    // Skip the theme's server-rendered placeholder ([data-theme-preorder-placeholder]):
    // it intentionally keeps a blank secondary line to reserve the app's ship-window
    // text, which normalization would otherwise strip.
    const descriptions = Array.from(
      template.querySelectorAll(
        '.restock-rocket-preorder-description:not([data-theme-preorder-placeholder])',
      ),
    );
    descriptions.forEach((description) => {
      const textBlocks = Array.from(
        description.querySelectorAll('.preorder-description-text'),
      );
      if (textBlocks.length) {
        textBlocks.forEach((textBlock) => {
          const currentText = textBlock.textContent || '';
          const targetHtml = formatPreorderMessageHtml(currentText);
          if (textBlock.innerHTML !== targetHtml) {
            textBlock.innerHTML = targetHtml;
          }
        });
      } else {
        const firstParagraph = description.querySelector('p');
        if (firstParagraph) {
          const currentText = firstParagraph.textContent || '';
          const targetHtml = formatPreorderMessageHtml(currentText);
          if (firstParagraph.innerHTML !== targetHtml) {
            firstParagraph.innerHTML = targetHtml;
          }
        }
      }

      const detailTexts = Array.from(
        description.querySelectorAll('.preorder-detail-text'),
      );
      detailTexts.forEach((detailText) => {
        const targetText = freeShippingText || DEFAULT_FREE_SHIPPING_TEXT;
        if ((detailText.textContent || '').trim() !== targetText) {
          detailText.textContent = targetText;
        }
      });
    });
  }

  function normalizeRichTextEmailLinks(template) {
    const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
    const richTextLinks = Array.from(
      template.querySelectorAll(
        '.product__accordion .accordion__content a, .product__description a',
      ),
    );

    richTextLinks.forEach((link) => {
      const href = (link.getAttribute('href') || '').trim();
      if (href) return;
      const normalizedText =
        link.textContent?.replace(/\s+/g, ' ').trim() || '';
      const emailMatch = normalizedText.match(emailPattern);
      if (!emailMatch) return;
      const emailAddress = emailMatch[0];
      link.setAttribute('href', `mailto:${emailAddress}`);
      link.setAttribute('aria-label', emailAddress);
    });
  }

  function syncTitleSaleBadge(template) {
    const badgeRow = template.querySelector('.pdp-product-title__badge-row');
    const saleBadge = template.querySelector('[data-pdp-title-sale-badge]');
    const preorderSlot = template.querySelector(
      '[data-pdp-title-preorder-badge-slot]',
    );
    const mediaSaleBadge = template.querySelector(
      '.pdp-media-sale-badge--overlay',
    );
    const mediaPreorderBadge = template.querySelector(
      '[data-pdp-media-preorder-badge]',
    );
    const mediaPreorderBadgeLabel = template.querySelector(
      '[data-pdp-media-preorder-badge-label]',
    );
    if (!badgeRow || !saleBadge) return;

    const priceElement = template.querySelector('[id^="price-"] .price');
    const hasSale =
      Boolean(priceElement?.classList.contains('price--on-sale')) &&
      Boolean(priceElement?.querySelector('.price-item--sale'));
    const preorderBadgeText = getPreorderBadgeText(template);
    const hasPreorderBadge = Boolean(preorderBadgeText);

    saleBadge.hidden = !hasSale;
    if (mediaSaleBadge) {
      mediaSaleBadge.hidden = !hasSale || hasPreorderBadge;
    }

    if (preorderSlot) {
      const preorderBadgeHtml = hasPreorderBadge
        ? `<span class="restock-rocket-preorder-badge preorder-badge-product pdp-product-title__preorder-badge">${escapeHtml(preorderBadgeText)}</span>`
        : '';

      preorderSlot.hidden = !preorderBadgeHtml;
      if (preorderSlot.innerHTML !== preorderBadgeHtml) {
        preorderSlot.innerHTML = preorderBadgeHtml;
      }
    }

    if (mediaPreorderBadge) {
      mediaPreorderBadge.hidden = !hasPreorderBadge;
    }

    if (mediaPreorderBadgeLabel && hasPreorderBadge) {
      mediaPreorderBadgeLabel.textContent = preorderBadgeText;
    }

    badgeRow.hidden = !(hasSale || hasPreorderBadge);
  }

  function hideDuplicatePreorderBadges(template) {
    // Restock Rocket injects its own PREORDER badge next to the price (often
    // duplicated, 2x). The theme renders a single PREORDER badge in the product
    // title, so hide Rocket's inline ones. CSS can't win here — Rocket's
    // stylesheet forces display:flex at a higher specificity than the theme's
    // hide rule — so set it inline, where !important beats any stylesheet rule.
    // The guard keeps this from re-triggering the MutationObserver in a loop.
    const dupes = template.querySelectorAll(
      '.restock-rocket-preorder-badge.preorder-badge-product:not(.pdp-product-title__preorder-badge)',
    );
    dupes.forEach((badge) => {
      if (badge.style.getPropertyValue('display') !== 'none') {
        badge.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function hidePreorderDescriptionPlaceholder(template) {
    // The theme renders a placeholder preorder description server-side to reserve
    // the space Restock Rocket's real one will take (no layout jump on injection).
    // Once Rocket's real description is present, drop the placeholder so they don't
    // stack. (custom.css also does this synchronously via :has() where supported;
    // this is the fallback. Both occupy the same container at a matching height, so
    // the swap doesn't shift the page.) The guard avoids a MutationObserver loop.
    const placeholder = template.querySelector('[data-theme-preorder-placeholder]');
    if (!placeholder) return;
    const hasReal = Boolean(
      template.querySelector(
        '.restock-rocket-preorder-description:not([data-theme-preorder-placeholder])',
      ),
    );
    if (hasReal && placeholder.style.getPropertyValue('display') !== 'none') {
      placeholder.style.setProperty('display', 'none', 'important');
    }
  }

  function syncPreorderStateOnTemplate(template) {
    const freeShippingText = getTemplateFreeShippingText(template);
    normalizePreorderDescriptionText(template, freeShippingText);
    syncFreeShippingSummaryText(template, freeShippingText);
    normalizeRichTextEmailLinks(template);
    hideDuplicatePreorderBadges(template);
    hidePreorderDescriptionPlaceholder(template);
    const hasPreorder =
      hasVisiblePreorderDescription(template) ||
      hasVisiblePreorderBadge(template) ||
      hasVisiblePreorderButton(template);
    const hasDualCta =
      !hasPreorder && hasVisibleDynamicCheckoutButton(template);
    const freeShippingSummary = template.querySelector(
      '.pdp-free-shipping-summary',
    );
    if (freeShippingSummary && freeShippingSummary.hidden !== hasPreorder) {
      freeShippingSummary.hidden = hasPreorder;
    }
    const hasVisibleFreeShipping = Boolean(
      freeShippingSummary && !freeShippingSummary.hidden,
    );
    template.classList.toggle('pdp-has-preorder', hasPreorder);
    template.classList.toggle('pdp-has-dual-cta', hasDualCta);
    template.classList.toggle('pdp-has-free-shipping', hasVisibleFreeShipping);
    syncTitleSaleBadge(template);
  }

  function schedulePreorderStateSync(template) {
    if (!template) return;
    if (preorderStateSyncFrames.has(template)) return;
    const frameId = window.requestAnimationFrame(() => {
      preorderStateSyncFrames.delete(template);
      syncPreorderStateOnTemplate(template);
    });
    preorderStateSyncFrames.set(template, frameId);
  }

  function initPreorderState(root = document) {
    // Safety net: reveal the cloaked free-shipping summary even if the sync below
    // never runs, so it can't stay hidden indefinitely. Normally the sync reveals
    // it immediately (with the canonical announcement-bar text); this only fires in
    // the rare case that path throws.
    window.setTimeout(() => {
      (root.querySelectorAll?.('.pdp-free-shipping-summary--pending') || []).forEach(
        (el) => el.classList.remove('pdp-free-shipping-summary--pending'),
      );
    }, 1000);
    const templates = collectMainProductTemplates(root);
    templates.forEach((template) => {
      syncPreorderStateOnTemplate(template);
      if (preorderStateObservers.has(template)) return;
      const observer = new MutationObserver(() => {
        schedulePreorderStateSync(template);
      });
      observer.observe(template, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      });
      preorderStateObservers.set(template, observer);
    });
  }

  const sizeGuideObservers = new WeakMap();

  function hasSizeGuideLink(legend) {
    return Boolean(
      legend.querySelector('.ks-chart-container .ks-chart-modal-link') ||
        legend.querySelector('.ks-chart-container ._ks_text'),
    );
  }

  // How long to keep the size legend cloaked before revealing it regardless,
  // for products where Kiwi never injects a chart (the majority). Charted
  // products reveal as soon as the link appears (see syncSizeGuideState).
  const SIZE_GUIDE_REVEAL_MS = 600;

  function revealSizeGuide(legend) {
    legend.classList.remove('size-guide-pending');
  }

  function syncSizeGuideState(legend) {
    const hasGuide = hasSizeGuideLink(legend);
    legend.classList.toggle('size-chart-has-guide', hasGuide);
    // Once Kiwi's link is in, the centered "Size: X  Size Chart" group is in
    // its final layout — reveal now, so the re-center happened while cloaked
    // (no visible shift).
    if (hasGuide) revealSizeGuide(legend);
  }

  function initSizeGuideState(root = document) {
    root.querySelectorAll?.('.size_chart_custom').forEach((legend) => {
      syncSizeGuideState(legend);
      if (sizeGuideObservers.has(legend)) return;
      const observer = new MutationObserver(() => {
        syncSizeGuideState(legend);
      });
      observer.observe(legend, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      sizeGuideObservers.set(legend, observer);
      // Safety net: reveal even if no chart ever loads, so the size label is
      // never hidden indefinitely.
      window.setTimeout(() => revealSizeGuide(legend), SIZE_GUIDE_REVEAL_MS);
    });
  }

  function initAll(root = document) {
    initAOS(root);
    initSwipers(root);
    initQuickViewDelegate();
    initPreorderState(root);
    initSizeGuideState(root);
  }

  onReady(() => initAll(document));

  // Theme editor: re-init newly injected sections
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });

  // Give AOS a final refresh once media is loaded
  window.addEventListener('load', () => {
    try {
      initAOS(document);
    } catch (e) {
      debugLog('AOS refresh on load skipped', e);
    }
  });
})();